import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    Signal,
    signal,
    untracked,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommunityEventsService } from '@core/community-events/services/community-events.service';
import { BackgroundColorDirective, ButtonDirective, ColorDirective, TypographyDirective } from '@dartcounter/ui';
import { ProfileBadgeUnlockedEvent } from '@dc-core/dc-backend/dc-events';
import { NavController } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { DotLottieWebWorkerComponent } from 'ngx-dotlottie-web/src/webworker';
import { LottieCacheService } from 'src/app/core/lottie/lottie-cache.service';

import { ModalService } from '../../services/modal.service';

@Component({
    selector: 'app-badge-unlocked-overlay',
    standalone: true,
    templateUrl: './badge-unlocked-overlay.component.html',
    imports: [
        CommonModule,
        TranslateModule,
        DotLottieWebWorkerComponent,
        ButtonDirective,
        BackgroundColorDirective,
        TypographyDirective,
        ColorDirective,
    ],
})
export class BadgeUnlockedOverlayComponent implements OnInit {
    private lottieCacheService: LottieCacheService = inject(LottieCacheService);
    private authService: AuthService = inject(AuthService);
    private communityEventsService: CommunityEventsService = inject(CommunityEventsService);
    private navController: NavController = inject(NavController);
    private destroyRef: DestroyRef = inject(DestroyRef);
    private modalService: ModalService = inject(ModalService);

    private readonly AND = $localize`:@@AND:and`;

    public badgeEvent: WritableSignal<ProfileBadgeUnlockedEvent | null> = signal(null);

    public overlayLottieData: WritableSignal<ArrayBuffer | null> = signal(null);
    public overlayDescription: Signal<string | null> = computed(() => {
        if (!this.badgeEvent()) return null;
        return this.badgeEvent().profile_badge.description;
    });
    public overlayPlayerNames: Signal<string | null> = computed(() => {
        if (!this.badgeEvent()) return null;
        const playerNames = this.badgeEvent()
            .users.map((user) => user.name)
            .filter((name) => !!name);
        if (!playerNames.length) return null;
        return this.getOverlayPlayerNamesText(playerNames);
    });
    public showOverlayProfileButton: Signal<boolean> = computed(() =>
        this.badgeEvent()?.users.some((user) => user.id == this.authService.user.id)
    );

    constructor() {
        effect(() => {
            const badgeEvent = this.badgeEvent();

            if (!!badgeEvent && badgeEvent.auto_hide_after_ms) {
                untracked(() => {
                    setTimeout(() => this.dismissOverlay(), badgeEvent?.auto_hide_after_ms);
                });
            }

            const lottieUrl = badgeEvent?.profile_badge.lottie_url;
            if (!lottieUrl) {
                this.overlayLottieData.set(null);
                return;
            }

            void this.loadOverlayLottie(lottieUrl);
        });
    }

    public ngOnInit(): void {
        this.communityEventsService.unlockedProfileBadge$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (!event || this.badgeEvent()) return;
                this.badgeEvent.set(event);
            });
    }

    public equipBadge(): void {
        this.dismissOverlay();
        this.modalService.dismiss();

        this.navController.navigateForward('profile', {
            queryParams: {
                showBadgeInstruction: true,
            },
        });
    }

    public dismissOverlay(): void {
        this.badgeEvent.set(null);
    }

    private getOverlayPlayerNamesText(playerNames: string[]): string {
        if (playerNames.length === 0) return '';
        if (playerNames.length === 1) return playerNames[0];
        if (playerNames.length === 2) return `${playerNames[0]} ${this.AND} ${playerNames[1]}`;
        return `${playerNames.slice(0, -1).join(', ')} ${this.AND} ${playerNames[playerNames.length - 1]}`;
    }

    private async loadOverlayLottie(src: string): Promise<void> {
        const buffer = await this.lottieCacheService.fetchBuffer(src);
        this.overlayLottieData.set(buffer);
    }
}
